<script>
import { isNav2026Enabled } from '@modules/navegacao-v2/utils/nav2026-enabled'
import Teleport from './teleport.vue'
import { resolveRouteHeader } from './resolve-route-header'

/**
 * <ConditionalTeleport to="alvo"> — teleporta o slot para `to` QUANDO o alvo
 * existe (navegação 2026 ligada); senão renderiza o conteúdo INLINE, no lugar da
 * própria view.
 *
 * Por quê: o `<TeleportTarget name="page-header-actions">` só existe na nav-bar
 * do Nav 2026 (`nav-bar-navigation.vue`, sob `v-if="isV2"`). Com a flag OFF, a
 * NavBar V1 não tem esse destino — o conteúdo teleportado vira órfão no bus e
 * some (tela sem filtros = inutilizável). Aqui, quando a flag está OFF, o header
 * é renderizado inline no topo da view (onde o `<Teleport>` está hoje).
 *
 * Genérico: não conhece `page-header-actions`. Deriva da flag Nav 2026 — reativa
 * via store getter, então virar a flag re-renderiza e troca de branch
 * automaticamente.
 *
 * Título/subtítulo do header inline (branch OFF) são AUTO-RESOLVIDOS da rota
 * atual casada contra a prop `routes` (as rotas de abas do módulo, ex.:
 * `crmRoutes()`) — genérico/universal: cada módulo passa as SUAS rotas. As props
 * `title`/`subtitle` são OVERRIDE explícito (têm prioridade). No branch ON,
 * título/subtítulo são ignorados (a nav-bar do Nav 2026 já exibe o header).
 *
 * O casamento (via `resolveRouteHeader`) replica o `isSelected` da nav-bar:
 * prefixo do nome da rota, primeiro match. Assim uma subrota herda o header da
 * aba (ex.: `crm.relationship.*` → `crm.relationship`), igual ao Nav 2026.
 *
 * Não guarda vnodes (o `$slots.default` é lido fresco a cada render); toda a
 * lógica de clonagem/keying continua no `teleport-target.vue`.
 */
export default {
  name: 'ConditionalTeleport',
  components: { Teleport },
  props: {
    to: { type: String, required: true },
    // Tag do wrapper no fallback inline.
    tag: { type: String, default: 'div' },
    // Fallback inline (Nav 2026 OFF): título/subtítulo à ESQUERDA, espelhando o
    // header do Nav 2026. OVERRIDE opcional — se vazio, resolve de `routes`.
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    // Rotas de abas do módulo (ex.: `crmRoutes()`) — fonte do título/subtítulo
    // resolvido pela rota atual quando `title`/`subtitle` não são passados.
    routes: { type: Array, default: () => [] },
  },
  computed: {
    // Reativo: quando `parameters/show` muda, recomputa e troca de branch.
    isEnabled() {
      return isNav2026Enabled(this.$store.getters['parameters/show'])
    },
    // Header derivado da rota atual (casada em `routes`, prefixo como a nav-bar).
    resolvedHeader() {
      const routeName = this.$route && this.$route.name
      return resolveRouteHeader(routeName, this.routes)
    },
    // Prop explícita vence; senão, o header resolvido da rota.
    resolvedTitle() {
      return this.title || this.resolvedHeader.title
    },
    resolvedSubtitle() {
      return this.subtitle || this.resolvedHeader.subtitle
    },
  },
  render(h) {
    const children = this.$slots.default
    if (this.isEnabled) {
      // children viram o $slots.default do <Teleport> interno (slot forwarding
      // do Vue 2: 3o arg de h(componente, data, children)).
      return h(Teleport, { props: { to: this.to } }, children)
    }

    // Alvo inexistente (NavBar V1): header inline no lugar da view, com o mesmo
    // layout do Nav 2026 — [título/subtítulo] à esquerda, ações à direita.
    const title = this.resolvedTitle
    const subtitle = this.resolvedSubtitle
    const titleNodes = []
    if (title) {
      titleNodes.push(
        h('h1', { class: 'conditional-teleport-inline__title' }, title)
      )
    }
    if (subtitle) {
      titleNodes.push(
        h('p', { class: 'conditional-teleport-inline__subtitle' }, subtitle)
      )
    }

    // Bloco de títulos com flex:1 cresce e empurra as ações para a direita —
    // mesmo vazio (sem title/subtitle), as ações ficam alinhadas à direita.
    const titlesBlock = h(
      'div',
      { class: 'conditional-teleport-inline__titles' },
      titleNodes
    )
    const actionsBlock = h(
      'div',
      { class: 'conditional-teleport-inline__actions' },
      children
    )

    return h(this.tag, { class: 'conditional-teleport-inline' }, [
      titlesBlock,
      actionsBlock,
    ])
  },
}
</script>

<style lang="sass">
// Fallback inline (Nav 2026 OFF): header no mesmo layout do Nav 2026 —
// [titulo/subtitulo] a esquerda, acoes a direita, com respiro para o conteudo.
.conditional-teleport-inline
  display: flex
  align-items: center
  justify-content: space-between
  gap: 16px
  margin-bottom: 16px

  &__titles
    flex: 1 1 auto
    min-width: 0

  &__title
    font-size: 24px
    font-weight: 700
    color: #030309
    margin: 0
    line-height: 1.2

  &__subtitle
    font-size: 13px
    color: #8e8e8e
    margin: 4px 0 0

  &__actions
    display: flex
    align-items: center
    gap: 12px
    flex-shrink: 0
</style>
